// ///////////////////////////////////////////////////////////
// tbGame: The game object that handles the main loop and
// all the event processing
//
// Copyright 2013-2015 CoolGames
// Last Changed: 3-9-2015
// ///////////////////////////////////////////////////////////
import { tbAudio } from "./tbAudio";
import { tbGraphics } from "./tbGraphics";
import { tbLoaderManager } from "./tbLoaderManager";
// ********************************************
// ** Dealing with the global game loop
// ********************************************
window["__timingStartTime"] = 0; // The time just before the step is started
window["__timingFinishTime"] = 0; // The time just after the step is finished
window["__timingStepTime"] = 0; // The time the step took
window["__timingBetweenTime"] = 0; // The time between the start of the current step and the finish of the previous
window["__timingWaitTime"] = 0; // The wait time when using setTimeout
window["__timingFrames"] = 60; // The desired number of frames per second when using setTimeout
window["__timingInterval"] = 0; // The minimal interval used in setTimeout (to give the system some time for processing)
window["__timingForceTimeout"] = false; // Whether to force using setTimeout (and not requestAnimationFrame)
// Define the callback function to be called in each step
window["__timingDoAStep"] = function () {
    // Remember the current time
    window["__timingStartTime"] = Date.now();
    window["__timingBetweenTime"] = window["__timingStartTime"] - window["__timingFinishTime"];
    // Perform a step in the loop
    tbGame.stepLoop();
    // Remember the time when it is finished
    window["__timingFinishTime"] = Date.now();
    window["__timingStepTime"] = window["__timingFinishTime"] - window["__timingStartTime"];
    // Set the correct wait time and requestthe next frame
    window["__timingWaitTime"] = Math.max(window["__timingInterval"], 1000 / window["__timingFrames"] - window["__timingStepTime"]);
    window["__timingRequestNextAnimationFrame"](window["__timingDoAStep"]);
};
// Define our own request animation frame function
(function () {
    // Default use the timeout function
    window["__timingRequestNextAnimationFrame"] =
        function (callback) {
            window.setTimeout(callback, window["__timingWaitTime"]);
        };
    // If request animation frame is supported, use that instead
    if (!window["__timingForceTimeout"]) {
        window["__timingRequestNextAnimationFrame"] = window.requestAnimationFrame ||
            window["webkitRequestAnimationFrame"] ||
            window["mozRequestAnimationFrame"] ||
            window["msRequestAnimationFrame"] ||
            window["oRequestAnimationFrame"] ||
            window["__timingRequestNextAnimationFrame"];
    }
}());
var TBGame = /** @class */ (function () {
    function TBGame(mainCanvas) {
        this.loadMomentsOrder = ["Load"];
        this.currentLoadMomentIndex = 0;
        if (mainCanvas) {
            this.mainCanvas = mainCanvas;
        }
        // Initialize the tokens
        this.clear();
        // Initialize the event handlers
        this._initEventCounters();
        this._initClick(this);
        this._initTouch(this);
        this._initKeyboard(this);
        this._initAlarm(this);
        this._initSpecial(this);
        this._initPause(this);
        this._initLoop();
        this.renderCallback = null;
    }
    Object.defineProperty(TBGame.prototype, "currentLoadMoment", {
        get: function () { return this.loadMomentsOrder[this.currentLoadMomentIndex] || ""; },
        enumerable: false,
        configurable: true
    });
    // Clears the game state, removing all tokens and elements
    TBGame.prototype.clear = function () {
        this._tokens = [];
        this._tokenOrderChanged = false;
        this._elements = [];
        this._elementOrderChanged = false;
        this._isPreventDefaultEnabled = true;
    };
    // Clears all events
    TBGame.prototype.clearAllEvents = function () {
        this.clearClickEvents();
        this.clearTouchEvents();
        this.clearKeyboardEvents();
        this.clearAlarmEvents();
        this.clearSpecialEvents();
    };
    // Sets the main canvas (required for finding touch event positions)
    TBGame.prototype.setMainCanvas = function (mainCanvas) {
        this.mainCanvas = mainCanvas;
    };
    // ********************************************
    // ** Event counters
    // ** For each class of events we maintain the number of tokens that
    // ** use it. We only run through the array when this number is positive
    // ********************************************
    // Initialize the event counters
    TBGame.prototype._initEventCounters = function () {
        this._numberBeginStepEvents = 0;
        this._numberClickEvents = 0;
        this._numberTouchEvents = 0;
        this._numberKeyEvents = 0;
        this._numberAlarmEvents = 0;
        this._numberSpecialEvents = 0;
        this._numberEndStepEvents = 0;
        this._numberImmediateClickEvents = 0;
        this._numberImmediateTouchEvents = 0;
        this._numberImmediateKeyEvents = 0;
        this._numberImmediateSpecialEvents = 0;
    };
    // Add to the event counters
    TBGame.prototype._addEventCounters = function (token) {
        if (token.handleBeginStep) {
            this._numberBeginStepEvents += 1;
        }
        if (token.handleClick) {
            this._numberClickEvents += 1;
        }
        if (token.handleTouchDown || token.handleTouchUp) {
            this._numberTouchEvents += 1;
        }
        if (token.handleMouseWheel) {
            this._numberTouchEvents += 1;
        }
        if (token.handleKeyDown || token.handleKeyUp) {
            this._numberKeyEvents += 1;
        }
        if (token.handleAlarm) {
            this._numberAlarmEvents += 1;
        }
        if (token.handleSpecial) {
            this._numberSpecialEvents += 1;
        }
        if (token.handleEndStep) {
            this._numberEndStepEvents += 1;
        }
        if (token.handleImmediateClick) {
            this._numberImmediateClickEvents += 1;
        }
        if (token.handleImmediateTouchDown || token.handleImmediateTouchUp) {
            this._numberImmediateTouchEvents += 1;
        }
        if (token.handleImmediateMouseWheel) {
            this._numberImmediateTouchEvents += 1;
        }
        if (token.handleImmediateKeyDown || token.handleImmediateKeyUp) {
            this._numberImmediateKeyEvents += 1;
        }
        if (token.handleImmediateSpecial) {
            this._numberImmediateSpecialEvents += 1;
        }
    };
    // Subtract from the event counters
    TBGame.prototype._subtractEventCounters = function (token) {
        if (token.handleBeginStep) {
            this._numberBeginStepEvents -= 1;
        }
        if (token.handleClick) {
            this._numberClickEvents -= 1;
        }
        if (token.handleTouchDown || token.handleTouchUp) {
            this._numberTouchEvents -= 1;
        }
        if (token.handleMouseWheel) {
            this._numberTouchEvents -= 1;
        }
        if (token.handleKeyDown || token.handleKeyUp) {
            this._numberKeyEvents -= 1;
        }
        if (token.handleAlarm) {
            this._numberAlarmEvents -= 1;
        }
        if (token.handleSpecial) {
            this._numberSpecialEvents -= 1;
        }
        if (token.handleEndStep) {
            this._numberEndStepEvents -= 1;
        }
        if (token.handleImmediateClick) {
            this._numberImmediateClickEvents -= 1;
        }
        if (token.handleImmediateTouchDown || token.handleImmediateTouchUp) {
            this._numberImmediateTouchEvents -= 1;
        }
        if (token.handleImmediateMouseWheel) {
            this._numberImmediateTouchEvents -= 1;
        }
        if (token.handleImmediateKeyDown || token.handleImmediateKeyUp) {
            this._numberImmediateKeyEvents -= 1;
        }
        if (token.handleImmediateSpecial) {
            this._numberImmediateSpecialEvents -= 1;
        }
    };
    // ********************************************
    // ** Dealing with the click
    // ********************************************
    // Initialize the click handling
    TBGame.prototype._initClick = function (self) {
        window.addEventListener("click", function (e) { self._doClick(e); }, false);
        this.clearClickEvents();
    };
    // Clears the click events
    TBGame.prototype.clearClickEvents = function () {
        this._clickInfo = { x: 0, y: 0 };
        this._clickAction = []; // The clicks since the last step
    };
    // Add a click event
    TBGame.prototype.addClickEvent = function (x, y) {
        var i, tok, handled;
        this._clickInfo.x = x;
        this._clickInfo.y = y;
        this._clickAction.push({ x: this._clickInfo.x, y: this._clickInfo.y });
        // Handle the immediate events
        if (this._numberImmediateClickEvents > 0) {
            for (i = this._tokens.length - 1; i >= 0; i -= 1) {
                tok = this._tokens[i];
                if (tok && tok.active && tok._paused <= 0 && tok.handleImmediateClick) {
                    handled = tok.handleImmediateClick(this._clickInfo.x, this._clickInfo.y);
                    if (handled === true) {
                        break;
                    }
                }
            }
        }
    };
    // Handle a click event
    TBGame.prototype._doClick = function (e) {
        var canvas, bbox, x, y;
        if (this.mainCanvas === undefined) {
            return;
        }
        if (this._paused > 0) {
            return;
        }
        canvas = this.mainCanvas;
        bbox = canvas.getBoundingClientRect();
        x = (e.clientX - bbox.left) * (canvas.width / bbox.width);
        y = (e.clientY - bbox.top) * (canvas.height / bbox.height);
        if (this._isPreventDefaultEnabled) {
            e.preventDefault();
        }
        this.addClickEvent(x, y);
    };
    // Send the correct click events to the tokens
    TBGame.prototype._handleClickEvents = function () {
        var c, i, tok, act;
        if (this._clickAction.length === 0) {
            return;
        }
        // Send the events to the tokens
        if (this._numberClickEvents > 0) {
            for (i = this._tokens.length - 1; i >= 0; i -= 1) {
                tok = this._tokens[i];
                if (tok && tok.active && tok._paused <= 0 && tok.handleClick) {
                    for (c = 0; c < this._clickAction.length; c += 1) {
                        act = this._clickAction[c];
                        if (act.handled !== true) {
                            act.handled = tok.handleClick(act.x, act.y);
                        }
                    }
                }
            }
        }
        // Clear the click events
        this._clickAction = [];
    };
    // Return the click x position
    TBGame.prototype.clickX = function () {
        return this._clickInfo.x;
    };
    // Return the click y position
    TBGame.prototype.clickY = function () {
        return this._clickInfo.y;
    };
    // ********************************************
    // ** Dealing with the touch
    // ********************************************
    // Initialize the touch handling
    TBGame.prototype._initTouch = function (self) {
        window.addEventListener("mousedown", function (e) { self._doMouseDown(e); }, false);
        window.addEventListener("mouseup", function (e) { self._doMouseUp(e); }, false);
        window.addEventListener("mousemove", function (e) { self._doMouseMove(e); }, false);
        window.addEventListener("touchstart", function (e) { self._doTouchStart(e); }, false);
        window.addEventListener("touchend", function (e) { self._doTouchEnd(e); }, false);
        window.addEventListener("touchmove", function (e) { self._doTouchMove(e); }, false);
        window.addEventListener("touchleave", function (e) { self._doTouchLeave(e); }, false);
        window.addEventListener("touchcancel", function (e) { self._doTouchCancel(e); }, false);
        window.addEventListener("mousewheel", function (e) { self._doMouseWheel(e); }, false); // mouse wheel IE9, Chrome, Safari, Opera
        window.addEventListener("DOMMouseScroll", function (e) { self._doMouseWheel(e); }, false); // Firefox
        this.clearTouchEvents();
        // Hack to avoid touch event also firing mouse events
        this._lastTouchDown = 0;
        this._lastTouchUp = 0;
    };
    // Clears the touch events
    TBGame.prototype.clearTouchEvents = function () {
        this._touchInfo = []; // The information about the touch event
        for (var i = 0; i < 16; i += 1) {
            this._touchInfo[i] = { id: -1, down: false, x: 0, y: 0 };
        }
        this._touchAction = []; // The finger that were pressed or released since the last step
    };
    // We have to determine the finger ourselves as it turns out
    // devices like iPad do not follow the numbering standards
    // for touch identifiers.
    TBGame.prototype._findFinger = function (id) {
        var finger = -1, i;
        // Check whether it exists
        for (i = 0; i < 16; i += 1) {
            if (this._touchInfo[i].id === id) {
                finger = i;
                break;
            }
        }
        // If not, find first one that is not down
        if (finger === -1) {
            for (i = 0; i < 16; i += 1) {
                if (!this._touchInfo[i].down) {
                    finger = i;
                    this._touchInfo[i].id = id;
                    break;
                }
            }
        }
        return finger;
    };
    // Add a touch move event
    TBGame.prototype._addTouchMove = function (id, x, y) {
        var finger, canvas, bbox;
        if (this.mainCanvas === undefined) {
            return;
        }
        finger = this._findFinger(id);
        if (finger === -1) {
            return;
        }
        canvas = this.mainCanvas;
        bbox = canvas.getBoundingClientRect();
        this._touchInfo[finger].x = (x - bbox.left) * (canvas.width / bbox.width);
        this._touchInfo[finger].y = (y - bbox.top) * (canvas.height / bbox.height);
    };
    // Add a touch down event
    TBGame.prototype._addTouchDownEvent = function (id) {
        var finger = this._findFinger(id), i, tok, handled;
        if (finger === -1) {
            return;
        }
        if (this._touchInfo[finger].down) {
            return;
        }
        this._touchAction.push({ finger: finger, x: this._touchInfo[finger].x, y: this._touchInfo[finger].y, down: true });
        this._touchInfo[finger].down = true;
        // Handle the immediate events
        if (this._numberImmediateTouchEvents > 0) {
            for (i = this._tokens.length - 1; i >= 0; i -= 1) {
                tok = this._tokens[i];
                if (tok && tok.active && tok._paused <= 0 && tok.handleImmediateTouchDown) {
                    handled = tok.handleImmediateTouchDown(finger, this._touchInfo[finger].x, this._touchInfo[finger].y);
                    if (handled === true) {
                        break;
                    }
                }
            }
        }
    };
    // Add a touch up event
    TBGame.prototype._addTouchUpEvent = function (id) {
        var finger = this._findFinger(id), i, tok, handled;
        if (finger === -1) {
            return;
        }
        if (!this._touchInfo[finger].down) {
            return;
        }
        this._touchAction.push({ finger: finger, x: this._touchInfo[finger].x, y: this._touchInfo[finger].y, down: false });
        this._touchInfo[finger].down = false;
        // Handle the immediate events
        if (this._numberImmediateTouchEvents > 0) {
            for (i = this._tokens.length - 1; i >= 0; i -= 1) {
                tok = this._tokens[i];
                if (tok && tok.active && tok._paused <= 0 && tok.handleImmediateTouchUp) {
                    handled = tok.handleImmediateTouchUp(finger, this._touchInfo[finger].x, this._touchInfo[finger].y);
                    if (handled === true) {
                        break;
                    }
                }
            }
        }
    };
    // Add a touch down event for a finger (rather than an id)
    TBGame.prototype.addTouchDownEvent = function (finger) {
        this._addTouchDownEvent(this._touchInfo[finger].id);
    };
    // Add a touch up event for a finger (rather than an id)
    TBGame.prototype.addTouchUpEvent = function (finger) {
        this._addTouchUpEvent(this._touchInfo[finger].id);
    };
    // Add a mouse wheel event
    TBGame.prototype.addMouseWheelEvent = function (delta) {
        var finger = 0, i, tok, handled;
        this._touchAction.push({ finger: finger, x: this._touchInfo[finger].x, y: this._touchInfo[finger].y, wheelDelta: delta < 0 ? -1 : 1, realDelta: delta });
        // Handle the immediate events
        if (this._numberImmediateTouchEvents > 0) {
            for (i = this._tokens.length - 1; i >= 0; i -= 1) {
                tok = this._tokens[i];
                if (tok && tok.active && tok._paused <= 0 && tok.handleImmediateMouseWheel) {
                    handled = tok.handleImmediateMouseWheel(delta < 0 ? -1 : 1, this._touchInfo[finger].x, this._touchInfo[finger].y, delta);
                    if (handled === true) {
                        break;
                    }
                }
            }
        }
    };
    // Handle a mouse button down event
    TBGame.prototype._doMouseDown = function (e) {
        if (this._paused > 0) {
            return;
        }
        if (this._isPreventDefaultEnabled) {
            e.preventDefault();
        }
        window.focus(); // Because we cancel this with the previous command
        if (this._lastTouchDown >= Date.now() - 1000) {
            return;
        } // Hack to avoid a mouse down soon after a touch down event
        this._addTouchMove(0, e.clientX, e.clientY);
        this._addTouchDownEvent(0);
    };
    // Handle a mouse up event
    TBGame.prototype._doMouseUp = function (e) {
        if (this._paused > 0) {
            return;
        }
        if (this._isPreventDefaultEnabled) {
            e.preventDefault();
        }
        if (this._lastTouchUp >= Date.now() - 1000) {
            return;
        } // Hack to avoid a mouse down soon after a touch up event
        this._addTouchMove(0, e.clientX, e.clientY);
        this._addTouchUpEvent(0);
    };
    // Handle a mouse move event
    TBGame.prototype._doMouseMove = function (e) {
        if (this._paused > 0) {
            return;
        }
        if (this._isPreventDefaultEnabled) {
            e.preventDefault();
        }
        this._addTouchMove(0, e.clientX, e.clientY);
    };
    // Handle a mouse wheel event
    TBGame.prototype._doMouseWheel = function (e) {
        var delta;
        if (this._paused > 0) {
            return;
        }
        if (this._isPreventDefaultEnabled) {
            e.preventDefault();
        }
        window.focus(); // Becaused we cancel this with the previous command
        delta = e.wheelDelta || -e.detail;
        this.addMouseWheelEvent(delta);
    };
    // Handle a touch start event
    TBGame.prototype._doTouchStart = function (e) {
        var touches = e.changedTouches, i;
        this._lastTouchDown = Date.now();
        if (this._paused > 0) {
            return;
        }
        if (this._isPreventDefaultEnabled) {
            e.preventDefault();
        }
        window.focus(); // Because we cancel this with the previous command
        for (i = 0; i < touches.length; i += 1) {
            this._addTouchMove(touches[i].identifier, touches[i].clientX, touches[i].clientY);
            this._addTouchDownEvent(touches[i].identifier);
        }
    };
    // Handle a touch end event
    TBGame.prototype._doTouchEnd = function (e) {
        var touches = e.changedTouches, i;
        this._lastTouchUp = Date.now();
        if (this._paused > 0) {
            return;
        }
        if (this._isPreventDefaultEnabled) {
            e.preventDefault();
        }
        for (i = 0; i < touches.length; i += 1) {
            this._addTouchMove(touches[i].identifier, touches[i].clientX, touches[i].clientY);
            this._addTouchUpEvent(touches[i].identifier);
        }
    };
    // Handle a touch leave event
    TBGame.prototype._doTouchLeave = function (e) {
        var touches = e.changedTouches, i;
        this._lastTouchUp = Date.now();
        if (this._paused > 0) {
            return;
        }
        if (this._isPreventDefaultEnabled) {
            e.preventDefault();
        }
        for (i = 0; i < touches.length; i += 1) {
            this._addTouchMove(touches[i].identifier, touches[i].clientX, touches[i].clientY);
            this._addTouchUpEvent(touches[i].identifier);
        }
    };
    // Handle a touch cancel event
    TBGame.prototype._doTouchCancel = function (e) {
        var touches = e.changedTouches, i;
        this._lastTouchUp = Date.now();
        if (this._paused > 0) {
            return;
        }
        if (this._isPreventDefaultEnabled) {
            e.preventDefault();
        }
        for (i = 0; i < touches.length; i += 1) {
            this._addTouchMove(touches[i].identifier, touches[i].clientX, touches[i].clientY);
            this._addTouchUpEvent(touches[i].identifier);
        }
    };
    // Handle a touch move event
    TBGame.prototype._doTouchMove = function (e) {
        var touches = e.changedTouches, i;
        if (this._paused > 0) {
            return;
        }
        if (this._isPreventDefaultEnabled) {
            e.preventDefault();
        }
        for (i = 0; i < touches.length; i += 1) {
            this._addTouchMove(touches[i].identifier, touches[i].clientX, touches[i].clientY);
        }
    };
    // Send the correct touch events to the tokens
    TBGame.prototype._handleTouchEvents = function () {
        var c, i, tok, act;
        if (this._touchAction.length === 0) {
            return;
        }
        // Send the events to the tokens
        if (this._numberTouchEvents > 0) {
            for (i = this._tokens.length - 1; i >= 0; i -= 1) {
                tok = this._tokens[i];
                if (tok && tok.active && tok._paused <= 0 && (tok.handleTouchDown || tok.handleTouchUp || tok.handleMouseWheel)) {
                    for (c = 0; c < this._touchAction.length; c += 1) {
                        act = this._touchAction[c];
                        if (act.handled !== true) {
                            if (act.wheelDelta !== undefined && tok.handleMouseWheel) {
                                act.handled = tok.handleMouseWheel(act.wheelDelta, act.x, act.y, act.realDelta);
                            }
                            else if (act.down && tok.handleTouchDown) {
                                act.handled = tok.handleTouchDown(act.finger, act.x, act.y);
                            }
                            else if (act.down !== undefined && !act.down && tok.handleTouchUp) {
                                act.handled = tok.handleTouchUp(act.finger, act.x, act.y);
                            }
                        }
                    }
                }
            }
        }
        // Clear the touch events
        this._touchAction = [];
    };
    // Check whether a touch finger is down
    TBGame.prototype.touchDown = function (finger) {
        return this._touchInfo[finger].down;
    };
    // Returns the touch id
    TBGame.prototype.touchId = function (finger) {
        return this._touchInfo[finger].id;
    };
    // Return the touch x position
    TBGame.prototype.touchX = function (finger) {
        return this._touchInfo[finger].x;
    };
    // Return the touch y position
    TBGame.prototype.touchY = function (finger) {
        return this._touchInfo[finger].y;
    };
    // ********************************************
    // ** Dealing with the keyboard
    // ********************************************
    // Initialize the keyboard handling
    TBGame.prototype._initKeyboard = function (self) {
        window.addEventListener("keydown", function (e) { self._doKeyDown(e); }, false);
        window.addEventListener("keyup", function (e) { self._doKeyUp(e); }, false);
        this.clearKeyboardEvents();
    };
    // Clears the keyboard handling
    TBGame.prototype.clearKeyboardEvents = function () {
        var i;
        this._keyDown = []; // Whether the key is currently down
        for (i = 0; i < 256; i += 1) {
            this._keyDown[i] = false;
        }
        this._keyAction = []; // The keys that were pressed or released since the last step
    };
    // Add a key down event
    TBGame.prototype.addKeyDownEvent = function (keyCode) {
        var i, tok, handled;
        if (this._keyDown[keyCode]) {
            return;
        }
        this._keyAction.push({ key: keyCode, down: true });
        this._keyDown[keyCode] = true;
        // Handle the immediate events
        if (this._numberImmediateKeyEvents > 0) {
            for (i = 0; i < this._tokens.length; i += 1) {
                tok = this._tokens[i];
                if (tok && tok.active && tok._paused <= 0 && tok.handleImmediateKeyDown) {
                    handled = tok.handleImmediateKeyDown(keyCode);
                    if (handled === true) {
                        break;
                    }
                }
            }
        }
    };
    // Add a key up event
    TBGame.prototype.addKeyUpEvent = function (keyCode) {
        var i, tok, handled;
        if (!this._keyDown[keyCode]) {
            return;
        }
        this._keyAction.push({ key: keyCode, down: false });
        this._keyDown[keyCode] = false;
        // Handle the immediate events
        if (this._numberImmediateKeyEvents > 0) {
            for (i = 0; i < this._tokens.length; i += 1) {
                tok = this._tokens[i];
                if (tok && tok.active && tok._paused <= 0 && tok.handleImmediateKeyUp) {
                    handled = tok.handleImmediateKeyUp(keyCode);
                    if (handled === true) {
                        break;
                    }
                }
            }
        }
    };
    // Handle a key down event
    TBGame.prototype._doKeyDown = function (e) {
        if (this._paused > 0) {
            return;
        }
        if ([32, 37, 38, 39, 40].indexOf(e.keyCode) > -1) {
            e.preventDefault();
        }
        this.addKeyDownEvent(e.keyCode);
    };
    // Handle a key up event
    TBGame.prototype._doKeyUp = function (e) {
        if (this._paused > 0) {
            return;
        }
        if ([32, 37, 38, 39, 40].indexOf(e.keyCode) > -1) {
            e.preventDefault();
        }
        this.addKeyUpEvent(e.keyCode);
    };
    // Send the correct key events to the tokens
    TBGame.prototype._handleKeyEvents = function () {
        var c, i, tok, act;
        if (this._keyAction.length === 0) {
            return;
        }
        // Send the events to the tokens
        if (this._numberKeyEvents > 0) {
            for (i = 0; i < this._tokens.length; i += 1) {
                tok = this._tokens[i];
                if (tok && tok.active && tok._paused <= 0 && (tok.handleKeyDown || tok.handleKeyUp)) {
                    for (c = 0; c < this._keyAction.length; c += 1) {
                        act = this._keyAction[c];
                        if (act.handled !== true) {
                            if (act.down && tok.handleKeyDown) {
                                act.handled = tok.handleKeyDown(act.key);
                            }
                            else if (!act.down && tok.handleKeyUp) {
                                act.handled = tok.handleKeyUp(act.key);
                            }
                        }
                    }
                }
            }
        }
        // Clear the keyboard events
        this._keyAction = [];
    };
    // Check whether a key is down
    TBGame.prototype.keyboardKeyDown = function (keyCode) {
        return this._keyDown[keyCode];
    };
    // ********************************************
    // ** Dealing with step events
    // ********************************************
    // Send a begin step event to the tokens
    TBGame.prototype._handleBeginStepEvents = function () {
        var i, tok;
        if (this._numberBeginStepEvents > 0) {
            for (i = 0; i < this._tokens.length; i += 1) {
                tok = this._tokens[i];
                if (tok && tok.handleBeginStep && tok.active && tok._paused <= 0) {
                    if (tok.handleBeginStep(this.stepTime) === true) {
                        return;
                    }
                }
            }
        }
    };
    // Send an end step event to the tokens
    TBGame.prototype._handleEndStepEvents = function () {
        var i, tok;
        if (this._numberEndStepEvents > 0) {
            for (i = 0; i < this._tokens.length; i += 1) {
                tok = this._tokens[i];
                if (tok && tok.handleEndStep && tok.active && tok._paused <= 0) {
                    if (tok.handleEndStep(this.stepTime) === true) {
                        return;
                    }
                }
            }
        }
    };
    // ********************************************
    // ** Dealing with alarm events
    // ********************************************
    // Initialize the alarm events
    TBGame.prototype._initAlarm = function (self) {
        this._alarmId = 0;
        this.clearAlarmEvents();
    };
    // Clears the alarm events
    TBGame.prototype.clearAlarmEvents = function () {
        this._alarmInfo = [];
        this._alarmAction = [];
    };
    // Find an alarm index
    TBGame.prototype._findAlarmIndex = function (id, token) {
        var i;
        if (token === undefined) {
            token = null;
        }
        for (i = 0; i < this._alarmInfo.length; i += 1) {
            if (this._alarmInfo[i].id === id && this._alarmInfo[i].token === token) {
                return i;
            }
        }
        return -1;
    };
    // Add a new alarm
    TBGame.prototype.addAlarm = function (time, repeats, token) {
        if (repeats === undefined) {
            repeats = 1;
        }
        if (token === undefined) {
            token = null;
        }
        while (this.existsAlarm(this._alarmId, token)) {
            this._alarmId += 1;
        }
        this._alarmInfo.push({ id: this._alarmId, time: time, repeats: repeats,
            token: token, timeLeft: time, repeatsLeft: repeats - 1, paused: 0 });
        this._alarmId += 1;
        return this._alarmId - 1;
    };
    // Remove an existing alarm
    TBGame.prototype.removeAlarm = function (id, token) {
        var ind = this._findAlarmIndex(id, token);
        if (ind >= 0) {
            this._alarmInfo[ind].id = undefined;
        }
    };
    // Checks whether an alarm with the id exists
    TBGame.prototype.existsAlarm = function (id, token) {
        var ind = this._findAlarmIndex(id, token);
        return ind >= 0;
    };
    // Set a new or existing alarm
    TBGame.prototype.setAlarm = function (id, time, repeats, token) {
        var n = this._alarmInfo.length, i;
        if (repeats === undefined) {
            repeats = 1;
        }
        if (token === undefined) {
            token = null;
        }
        // Check whether it exists
        for (i = 0; i < this._alarmInfo.length; i += 1) {
            if (this._alarmInfo[i].id === id && this._alarmInfo[i].token === token) {
                n = i;
            }
        }
        // Find an unused spot
        if (n === this._alarmInfo.length) {
            for (i = 0; i < this._alarmInfo.length; i += 1) {
                if (this._alarmInfo[i].id === undefined) {
                    n = i;
                }
            }
        }
        this._alarmInfo[n] = { id: id, time: time, repeats: repeats,
            token: token, timeLeft: time, repeatsLeft: repeats - 1, paused: 0 };
    };
    // Clear an existing alarm
    TBGame.prototype.clearAlarm = function (id, token) {
        var ind = this._findAlarmIndex(id, token);
        if (ind >= 0) {
            this._alarmInfo[ind].timeLeft = 0;
            this._alarmInfo[ind].repeatsLeft = 0;
        }
    };
    // Clear all alarms
    TBGame.prototype.clearAllAlarms = function () {
        var i;
        for (i = 0; i < this._alarmInfo.length; i += 1) {
            this._alarmInfo[i].timeLeft = 0;
            this._alarmInfo[i].repeatsLeft = 0;
        }
    };
    // Get an existing alarm
    TBGame.prototype.getAlarm = function (id, token) {
        var ind = this._findAlarmIndex(id, token);
        if (ind >= 0) {
            return this._alarmInfo[ind].timeLeft;
        }
        else {
            return undefined;
        }
    };
    // Get an existing alarm percentage
    TBGame.prototype.getAlarmPercentage = function (id, token) {
        var ind = this._findAlarmIndex(id, token);
        if (ind >= 0) {
            return 100 * this._alarmInfo[ind].timeLeft / this._alarmInfo[ind].time;
        }
        else {
            return undefined;
        }
    };
    // Pauses an alarm
    TBGame.prototype.pauseAlarm = function (id, token) {
        var ind = this._findAlarmIndex(id, token);
        if (ind >= 0) {
            this._alarmInfo[ind].paused += 1;
        }
    };
    // Resumes an alarm
    TBGame.prototype.resumeAlarm = function (id, token) {
        var ind = this._findAlarmIndex(id, token);
        if (ind >= 0) {
            this._alarmInfo[ind].paused = Math.max(0, this._alarmInfo[ind].paused - 1);
        }
    };
    // Resumes an alarm forced
    TBGame.prototype.resumeForcedAlarm = function (id, token) {
        var ind = this._findAlarmIndex(id, token);
        if (ind >= 0) {
            this._alarmInfo[ind].paused = 0;
        }
    };
    // Returns whether the alarm is paused
    TBGame.prototype.isPausedAlarm = function (id, token) {
        var ind = this._findAlarmIndex(id, token);
        if (ind >= 0) {
            return this._alarmInfo[ind].paused > 0;
        }
        else {
            return false;
        }
    };
    // Pause all alarms
    TBGame.prototype.pauseAllAlarms = function () {
        var i;
        for (i = 0; i < this._alarmInfo.length; i += 1) {
            this._alarmInfo[i].paused += 1;
        }
    };
    // Resume all alarms
    TBGame.prototype.resumeAllAlarms = function () {
        var i;
        for (i = 0; i < this._alarmInfo.length; i += 1) {
            this._alarmInfo[i].paused = Math.max(0, this._alarmInfo[i].paused - 1);
        }
    };
    // Resume all alarms forced
    TBGame.prototype.resumeForcedAllAlarms = function () {
        var i;
        for (i = 0; i < this._alarmInfo.length; i += 1) {
            this._alarmInfo[i].paused = 0;
        }
    };
    // Update the alarms
    TBGame.prototype._updateAlarms = function (timePassed) {
        var i, alarm;
        this._alarmAction.length = 0;
        for (i = 0; i < this._alarmInfo.length; i += 1) {
            alarm = this._alarmInfo[i];
            if (alarm.id !== undefined && alarm.paused === 0 && (alarm.timeLeft > 0 || alarm.repeatsLeft > 0)) {
                alarm.timeLeft -= timePassed;
                if (alarm.timeLeft <= 0) {
                    this._alarmAction.push({ id: alarm.id, token: alarm.token });
                    if (alarm.repeatsLeft > 0) {
                        alarm.repeatsLeft -= 1;
                        alarm.timeLeft += alarm.time;
                    }
                    else {
                        alarm.timeLeft = 0;
                    }
                }
            }
        }
    };
    // Send alarm events to the tokens
    TBGame.prototype._handleAlarmEvents = function () {
        var i, tok, j, act;
        this._updateAlarms(this.stepTime);
        if (this._numberAlarmEvents > 0 && this._alarmAction.length > 0) {
            for (i = 0; i < this._tokens.length; i += 1) {
                tok = this._tokens[i];
                if (tok && tok.handleAlarm && tok.active) {
                    for (j = 0; j < this._alarmAction.length; j += 1) {
                        act = this._alarmAction[j];
                        if (act.handled !== true && (act.token === null || act.token === tok)) {
                            act.handled = tok.handleAlarm(act.id);
                        }
                    }
                }
            }
        }
    };
    // ********************************************
    // ** Dealing with special events
    // ********************************************
    // Initialize the special events
    TBGame.prototype._initSpecial = function (self) {
        this.clearSpecialEvents();
    };
    // Clear the special events
    TBGame.prototype.clearSpecialEvents = function () {
        this._specialAction = [];
    };
    // Add a special event
    TBGame.prototype.addSpecial = function (id, args, token) {
        var i, tok, handled;
        if (token === undefined) {
            token = null;
        }
        this._specialAction.push({ id: id, args: args, token: token });
        // Handle the immediate events
        if (this._numberImmediateSpecialEvents > 0) {
            for (i = 0; i < this._tokens.length; i += 1) {
                tok = this._tokens[i];
                if (tok && tok.active && tok._paused <= 0 && tok.handleImmediateSpecial && (token === null || token === tok)) {
                    handled = tok.handleImmediateSpecial(id, args);
                    if (handled === true) {
                        break;
                    }
                }
            }
        }
    };
    // Send special events to the tokens
    TBGame.prototype._handleSpecialEvents = function () {
        var i, j, tok, act;
        if (this._numberSpecialEvents > 0 && this._specialAction.length > 0) {
            for (i = 0; i < this._tokens.length; i += 1) {
                tok = this._tokens[i];
                if (tok && tok.handleSpecial && tok.active && tok._paused <= 0) {
                    for (j = 0; j < this._specialAction.length; j += 1) {
                        act = this._specialAction[j];
                        if (act.handled !== true && (act.token === null || act.token === tok)) {
                            act.handled = tok.handleSpecial(act.id, act.args);
                        }
                    }
                }
            }
        }
        this._specialAction.length = 0;
    };
    // ********************************************
    // ** Dealing with drawing events
    // ********************************************
    // Draws an element
    TBGame.prototype._drawElement = function (ind) {
        var element = this._elements[ind];
        if (!element.visible) {
            return;
        }
        // Set the correct canvas
        if (element.canvas !== undefined && element.canvas !== tbGraphics.canvas) {
            tbGraphics.setCanvas(element.canvas);
        }
        // Now draw it if required
        if (tbGraphics.canvas.drawTokens === false && element.forceDraw !== true) {
            return;
        }
        if (element._kind === 0) { // Image
            if (element._paused <= 0) {
                element.subImage += element.animSpeed * this.stepTime / 1000;
            }
            if (element.xscale === 1 && element.yscale === 1 && element.angle === 0) {
                if (element.alpha === 1) {
                    element.image.drawSimple(element.subImage, element.x, element.y);
                }
                else {
                    element.image.drawAlpha(element.subImage, element.x, element.y, element.alpha);
                }
            }
            else {
                element.image.drawExt(element.subImage, element.x, element.y, element.xscale, element.yscale, element.angle, element.alpha);
            }
        }
        if (element._kind === 1) { // Text
            if (element.xscale === 1 && element.yscale === 1 && element.angle === 0) {
                if (element.alpha === 1) {
                    element.font.drawSimple(element.text, element.x, element.y);
                }
                else {
                    element.font.drawAlpha(element.text, element.x, element.y, element.alpha);
                }
            }
            else {
                element.font.drawExt(element.text, element.x, element.y, element.xscale, element.yscale, element.angle, element.alpha);
            }
        }
    };
    // Draws a token
    TBGame.prototype._drawToken = function (ind) {
        var tok = this._tokens[ind];
        if (!tok.visible) {
            return;
        }
        // Set the correct canvas
        if (tok.canvas !== undefined && tok.canvas !== tbGraphics.canvas) {
            tbGraphics.setCanvas(tok.canvas);
        }
        // Now draw it if required
        if (tbGraphics.canvas.drawTokens === false && tok.forceDraw !== true) {
            return;
        }
        if (tok.handleDraw) {
            return tok.handleDraw();
        }
    };
    // Handle the drawing and send draw events to the tokens
    TBGame.prototype._handleDrawEvents = function () {
        var nt = 0, ne = 0;
        while (nt < this._tokens.length || ne < this._elements.length) {
            if (ne === this._elements.length) {
                if (this._drawToken(nt) === true) {
                    return;
                }
                nt += 1;
            }
            else if (nt === this._tokens.length) {
                this._drawElement(ne);
                ne += 1;
            }
            else if (this._elements[ne].layer > this._tokens[nt].layer ||
                (this._elements[ne].layer === this._tokens[nt].layer &&
                    this._elements[ne].depth > this._tokens[nt].depth)) {
                this._drawElement(ne);
                ne += 1;
            }
            else {
                if (this._drawToken(nt) === true) {
                    return;
                }
                nt += 1;
            }
        }
    };
    // ********************************************
    // ** Dealing with pausing
    // ********************************************
    // Initialize the pausing
    TBGame.prototype._initPause = function (self) {
        this._paused = 0;
        this._pausedDraw = false;
    };
    // Pause the loop
    TBGame.prototype.pause = function (pausedDraw) {
        this._paused += 1;
        if (pausedDraw === undefined) {
            pausedDraw = false;
        }
        this._pausedDraw = pausedDraw;
    };
    // Resume the loop
    TBGame.prototype.resume = function () {
        if (this._paused === 0) {
            return;
        }
        this.lastTime = Date.now(); // To avoid jumps in game time and big step time
        this._paused -= 1;
    };
    // Resume the loop
    TBGame.prototype.resumeForced = function () {
        if (this._paused === 0) {
            return;
        }
        this.lastTime = Date.now(); // To avoid jumps in game time and big step time
        this._paused = 0;
    };
    // Returns whether the loop is paused
    TBGame.prototype.isPaused = function () {
        return this._paused > 0;
    };
    TBGame.prototype.supportsRequestAnimationFrame = function () {
        // Returns whether the system supports the use of request animation frame (in some form)
        return window.requestAnimationFrame !== undefined ||
            window["webkitRequestAnimationFrame"] !== undefined ||
            window["mozRequestAnimationFrame"] !== undefined ||
            window["msRequestAnimationFrame"] !== undefined ||
            window["oRequestAnimationFrame"] !== undefined;
    };
    TBGame.prototype.forceUseTimeout = function (force) {
        // forces the use of the timeout function instead of request animation frame (or not)
        window["__timingForceTimeout"] = force;
        window["__timingDefineFunction"]();
    };
    TBGame.prototype.setLoopSpeed = function (framesPerSecond) {
        // Sets the number of frames per second (when requestAnimationFrame does not exist)
        window["__timingFrames"] = framesPerSecond;
    };
    TBGame.prototype.setLoopInterval = function (interval) {
        // Sets the minimal interval for setTimeout (when requestAnimationFrame does not exist)
        window["__timingInterval"] = interval;
    };
    // Initialize the loop
    TBGame.prototype._initLoop = function () {
        // Initialize time (all in milliseconds)
        this.startTime = Date.now(); // The time the game started
        this.lastTime = this.startTime; // The time for the last step
        this.gameTime = 0; // The time the game has run so far (sum of step times)
        this.stepTime = 0; // The time of the last step
        this.maxStepTime = 200; // The maximal step time passed back in the step events
        this.totalSteps = 0; // The total number of steps since the start
        // Set the first loop call using out own version of window.requestNext AnimationFram
        window["__timingRequestNextAnimationFrame"](window["__timingDoAStep"]);
    };
    // Function to order the tokens and elements and clean them up
    TBGame.prototype._order = function () {
        function sortfunction(a, b) {
            return (a.layer === b.layer) ? b.depth - a.depth : ((a.layer > b.layer) ? -1 : 1);
        }
        var i, n;
        // Handle the tokens
        n = 0;
        for (i = 0; i < this._tokens.length; i += 1) {
            if (this._tokens[i]) {
                // activate when new
                if (this._tokens[i]._activateNew) {
                    this._tokens[i]._activateNew = false;
                    this._tokens[i].active = true;
                }
                // shift it in the array
                this._tokens[n] = this._tokens[i];
                n += 1;
            }
        }
        this._tokens.length = n;
        if (this._tokenOrderChanged) {
            this._tokens.sort(sortfunction);
        }
        this._tokenOrderChanged = false;
        // Handle the elements
        n = 0;
        for (i = 0; i < this._elements.length; i += 1) {
            if (this._elements[i]) {
                this._elements[n] = this._elements[i];
                n += 1;
            }
        }
        this._elements.length = n;
        if (this._elementOrderChanged) {
            this._elements.sort(sortfunction);
        }
        this._elementOrderChanged = false;
    };
    TBGame.prototype.stepLoop = function () {
        // Handle paused mode
        if (this._paused > 0) {
            if (this._pausedDraw) {
                this._order();
                this._handleDrawEvents();
            }
            return;
        }
        // Calculate the time passed
        var newTime = Date.now();
        // Solve a strange bugin which the time passed becomes NaN
        if (typeof newTime !== "number") {
            newTime = this.lastTime;
        }
        this.stepTime = Math.min(this.maxStepTime, newTime - this.lastTime);
        this.gameTime += this.stepTime;
        this.totalSteps += 1;
        // Handle the sounds
        tbAudio === null || tbAudio === void 0 ? void 0 : tbAudio.handleStep(this.stepTime);
        // Handle the events
        this._handleBeginStepEvents();
        this._handleClickEvents();
        this._handleTouchEvents();
        this._handleKeyEvents();
        this._handleAlarmEvents();
        this._handleSpecialEvents();
        this._handleEndStepEvents();
        // Order and clean up
        this._order();
        // Finally draw
        this._handleDrawEvents();
        if (this.renderCallback) {
            this.renderCallback();
        }
        // Update the time
        this.lastTime = newTime;
    };
    TBGame.prototype.preLoadImages = function () {
        var images = tbLoaderManager.images;
        for (var i = 0; i < images.length; i++) {
            images[i].add();
        }
        if (this.renderCallback) {
            this.renderCallback();
        }
        for (var i = 0; i < images.length; i++) {
            images[i].remove();
        }
    };
    // ********************************************
    // ** Adding and removing tokens
    // ********************************************
    // Add a token to the game; the optional activate argument indicates whether to activate it immediately
    TBGame.prototype.addToken = function (token, activate) {
        // Fill in properties if they are missing
        if (token.group === undefined) {
            token.group = 0;
        }
        if (token.visible === undefined) {
            token.visible = true;
        }
        if (token.active === undefined) {
            token.active = true;
        }
        if (token.depth === undefined) {
            token.depth = 0;
        }
        if (token.layer === undefined) {
            token.layer = 0;
        }
        if (token._paused === undefined) {
            token._paused = 0;
        }
        if (token._tags === undefined) {
            token._tags = [];
        }
        // Set activation
        token._activateNew = false;
        if (activate !== undefined && activate === false) {
            token._activateNew = true;
            token.active = false;
        }
        this._tokens.push(token);
        this._tokenOrderChanged = true;
        this._addEventCounters(token);
        // Add it to the array
        if (token.handleAdd) {
            token.handleAdd();
        }
    };
    // Change the depth of a token (also when it is not in the game!)
    TBGame.prototype.setTokenDepth = function (token, depth) {
        if (token.depth !== depth) {
            this._tokenOrderChanged = true;
        }
        token.depth = depth;
    };
    // Change the layer of a token (also when it is not in the game!)
    TBGame.prototype.setTokenLayer = function (token, layer) {
        if (token.layer !== layer) {
            this._tokenOrderChanged = true;
        }
        token.layer = layer;
    };
    // Clears the tags of a token
    TBGame.prototype.clearTokenTags = function (token) {
        token._tags = [];
    };
    // Returns (a copy of) the tags of a token
    TBGame.prototype.getTokenTags = function (token) {
        var t, tags = [];
        if (token._tags === undefined) {
            token._tags = [];
        }
        for (t = token._tags.length - 1; t >= 0; t -= 1) {
            tags.push(token._tags[t]);
        }
        return tags;
    };
    // Add tags to a token
    TBGame.prototype.addTokenTags = function (token, tags) {
        var t;
        tags = [].concat(tags); // Such that it also works for a single value
        if (token._tags === undefined) {
            token._tags = [];
        }
        for (t = tags.length - 1; t >= 0; t -= 1) {
            if (token._tags.indexOf(tags[t]) < 0) {
                token._tags.push(tags[t]);
            }
        }
    };
    // Remove tags from a token
    TBGame.prototype.removeTokenTags = function (token, tags) {
        var t, ind;
        tags = [].concat(tags); // Such that it also works for a single value
        if (token._tags === undefined) {
            token._tags = [];
        }
        for (t = tags.length - 1; t >= 0; t -= 1) {
            ind = token._tags.indexOf(tags[t]);
            if (ind >= 0) {
                token._tags.splice(ind, 1);
            }
        }
    };
    // Returns all tokens that contain the tag or satisfy the select function
    TBGame.prototype.findTokens = function (arg) {
        var tokens = [], i, token;
        if (arg === undefined || arg === "all" || arg === "master") {
            for (i = 0; i < this._tokens.length; i += 1) {
                token = this._tokens[i];
                if (token !== undefined) {
                    tokens.push(token);
                }
            }
        }
        else if (typeof arg === "function") {
            for (i = 0; i < this._tokens.length; i += 1) {
                token = this._tokens[i];
                if (token !== undefined && arg(token)) {
                    tokens.push(token);
                }
            }
        }
        else {
            for (i = 0; i < this._tokens.length; i += 1) {
                token = this._tokens[i];
                if (token !== undefined && token._tags.indexOf(arg) >= 0) {
                    tokens.push(token);
                }
            }
        }
        return tokens;
    };
    // Find all tokens (for compatibility)
    TBGame.prototype.findAllTokens = function () {
        return this.findTokens();
    };
    // Pause a single token
    TBGame.prototype.pauseToken = function (token) {
        token._paused += 1;
    };
    // Pause a number of tokens
    TBGame.prototype.pauseTokens = function (arg) {
        var i, tokens = this.findTokens(arg);
        for (i = 0; i < tokens.length; i += 1) {
            this.pauseToken(tokens[i]);
        }
    };
    // Resume a single token
    TBGame.prototype.resumeToken = function (token) {
        token._paused = Math.max(0, token._paused - 1);
    };
    // Resume a number of tokens
    TBGame.prototype.resumeTokens = function (arg) {
        var i, tokens = this.findTokens(arg);
        for (i = 0; i < tokens.length; i += 1) {
            this.resumeToken(tokens[i]);
        }
    };
    // Force resume a single token
    TBGame.prototype.resumeForcedToken = function (token) {
        token._paused = 0;
    };
    // Force resume a number of tokens
    TBGame.prototype.resumeForcedTokens = function (arg) {
        var i, tokens = this.findTokens(arg);
        for (i = 0; i < tokens.length; i += 1) {
            this.resumeForcedToken(tokens[i]);
        }
    };
    // Remove a token from the game
    TBGame.prototype.removeToken = function (token) {
        var ind = this._tokens.indexOf(token);
        if (ind < 0) {
            return;
        }
        if (this._tokens[ind].handleRemove) {
            this._tokens[ind].handleRemove();
        }
        this._subtractEventCounters(this._tokens[ind]);
        this._tokens[ind] = undefined;
    };
    // Removes all tokens that satisfy the argument (tag or function or nothing)
    TBGame.prototype.removeTokens = function (arg) {
        var i, tokens = this.findTokens(arg);
        for (i = 0; i < tokens.length; i += 1) {
            this.removeToken(tokens[i]);
        }
    };
    // Remove all tokens (for compatibility)
    TBGame.prototype.removeAllTokens = function () {
        this.removeTokens();
    };
    // Counting all tokens that satisfy the argument (tag or function or nothing)
    TBGame.prototype.countTokens = function (arg) {
        var tokens = this.findTokens(arg);
        return tokens.length;
    };
    // Count all tokens (for compatibility)
    TBGame.prototype.countAllTokens = function () {
        this.countTokens();
    };
    // ********************************************
    // ** Adding and removing images
    // ********************************************
    // Add an image to the game
    TBGame.prototype.addImage = function (image, subImage, animSpeed, x, y, depth, layer) {
        return this.addImageExt(image, subImage, animSpeed, x, y, 1, 1, 0, 1, depth, layer);
    };
    // Add a simple image to the game
    TBGame.prototype.addImageSimple = function (image, x, y, depth, layer) {
        return this.addImageExt(image, 0, 0, x, y, 1, 1, 0, 1, depth, layer);
    };
    // Add an image with alpha value to the game
    TBGame.prototype.addImageAlpha = function (image, subImage, animSpeed, x, y, alpha, depth, layer) {
        return this.addImageExt(image, subImage, animSpeed, x, y, 1, 1, 0, alpha, depth, layer);
    };
    // Add an image with alpha and transformation to the game
    TBGame.prototype.addImageExt = function (image, subImage, animSpeed, x, y, xscale, yscale, angle, alpha, depth, layer) {
        if (layer === undefined) {
            layer = 0;
        }
        this._elements.push({ _kind: 0, image: image, subImage: subImage, animSpeed: animSpeed, visible: true,
            x: x, y: y, xscale: xscale, yscale: yscale, angle: angle, alpha: alpha, depth: depth, layer: layer,
            _paused: 0, _tags: [] });
        this._elementOrderChanged = true;
        return this._elements[this._elements.length - 1];
    };
    // Add an existing image token to the game
    TBGame.prototype.addImageToken = function (imageToken) {
        this._elements.push(imageToken);
        this._elementOrderChanged = true;
    };
    // Change the depth of an image token
    TBGame.prototype.setImageDepth = function (imageToken, depth) {
        this._elementOrderChanged = (imageToken.depth !== depth);
        imageToken.depth = depth;
    };
    // Change the layer of an image token
    TBGame.prototype.setImageLayer = function (imageToken, layer) {
        this._elementOrderChanged = (imageToken.layer !== layer);
        imageToken.layer = layer;
    };
    // Clears the tags of an image token
    TBGame.prototype.clearImageTags = function (imageToken) {
        imageToken._tags = [];
    };
    // Returns (a copy of) the tags of an image token
    TBGame.prototype.getImageTags = function (imageToken) {
        var t, tags = [];
        if (imageToken._tags === undefined) {
            imageToken._tags = [];
        }
        for (t = imageToken._tags.length - 1; t >= 0; t -= 1) {
            tags.push(imageToken._tags[t]);
        }
        return tags;
    };
    // Add tags to an image token
    TBGame.prototype.addImageTags = function (imageToken, tags) {
        var t;
        tags = [].concat(tags); // Such that it also works for a single value
        if (imageToken._tags === undefined) {
            imageToken._tags = [];
        }
        for (t = tags.length - 1; t >= 0; t -= 1) {
            if (imageToken._tags.indexOf(tags[t]) < 0) {
                imageToken._tags.push(tags[t]);
            }
        }
    };
    // Remove tags from an image token
    TBGame.prototype.removeImageTags = function (imageToken, tags) {
        var t, ind;
        tags = [].concat(tags); // Such that it also works for a single value
        if (imageToken._tags === undefined) {
            imageToken._tags = [];
        }
        for (t = tags.length - 1; t >= 0; t -= 1) {
            ind = imageToken._tags.indexOf(tags[t]);
            if (ind >= 0) {
                imageToken._tags.splice(ind, 1);
            }
        }
    };
    // Returns all images that contain the tag or satisfy the select function
    TBGame.prototype.findImages = function (arg) {
        var imageTokens = [], i, imageToken;
        if (arg === undefined || arg === "all" || arg === "master") {
            for (i = 0; i < this._elements.length; i += 1) {
                imageToken = this._elements[i];
                if (imageToken !== undefined && imageToken._kind === 0) {
                    imageTokens.push(imageToken);
                }
            }
        }
        else if (typeof arg === "function") {
            for (i = 0; i < this._elements.length; i += 1) {
                imageToken = this._elements[i];
                if (imageToken !== undefined && imageToken._kind === 0 && arg(imageToken)) {
                    imageTokens.push(imageToken);
                }
            }
        }
        else {
            for (i = 0; i < this._elements.length; i += 1) {
                imageToken = this._elements[i];
                if (imageToken !== undefined && imageToken._kind === 0 && imageToken._tags.indexOf(arg) >= 0) {
                    imageTokens.push(imageToken);
                }
            }
        }
        return imageTokens;
    };
    // Find all image tokens (for compatibility)
    TBGame.prototype.findAllImages = function () {
        return this.findImages();
    };
    // Pause a single image token
    TBGame.prototype.pauseImage = function (imageToken) {
        imageToken._paused += 1;
    };
    // Pause a number of image tokens
    TBGame.prototype.pauseImages = function (arg) {
        var i, imageTokens = this.findImages(arg);
        for (i = 0; i < imageTokens.length; i += 1) {
            this.pauseImage(imageTokens[i]);
        }
    };
    // Resume a single image token
    TBGame.prototype.resumeImage = function (imageToken) {
        imageToken._paused = Math.max(0, imageToken._paused - 1);
    };
    // Resume a number of image tokens
    TBGame.prototype.resumeImages = function (arg) {
        var i, imageTokens = this.findImages(arg);
        for (i = 0; i < imageTokens.length; i += 1) {
            this.resumeImage(imageTokens[i]);
        }
    };
    // Force resume a single image token
    TBGame.prototype.resumeForcedImage = function (imageToken) {
        imageToken._paused = 0;
    };
    // Force resume a number of images token
    TBGame.prototype.resumeForcedImages = function (arg) {
        var i, imageTokens = this.findImages(arg);
        for (i = 0; i < imageTokens.length; i += 1) {
            this.resumeForcedImage(imageTokens[i]);
        }
    };
    // Remove an image token from the game
    TBGame.prototype.removeImage = function (imageToken) {
        var ind = this._elements.indexOf(imageToken);
        if (ind < 0) {
            return;
        }
        this._elements[ind] = undefined;
    };
    // Removes all image tokens that satisfy the argument (tag or function or nothing)
    TBGame.prototype.removeImages = function (arg) {
        var i, imageTokens = this.findImages(arg);
        for (i = 0; i < imageTokens.length; i += 1) {
            this.removeImage(imageTokens[i]);
        }
    };
    // Remove all image tokens (for compatibility)
    TBGame.prototype.removeAllImages = function () {
        this.removeImages();
    };
    // Counting all image tokens that satisfy the argument (tag or function or nothing)
    TBGame.prototype.countImages = function (arg) {
        var imageTokens = this.findImages(arg);
        return imageTokens.length;
    };
    // Count all image tokens (for compatibility)
    TBGame.prototype.countAllImages = function () {
        this.countImages();
    };
    // ********************************************
    // ** Adding and removing texts
    // ********************************************
    // Add a simple text to the game
    TBGame.prototype.addTextSimple = function (text, font, x, y, depth, layer) {
        return this.addTextExt(text, font, x, y, 1, 1, 0, 1, depth, layer);
    };
    // Add a text with alpha value to the game
    TBGame.prototype.addTextAlpha = function (text, font, x, y, alpha, depth, layer) {
        return this.addTextExt(text, font, x, y, 1, 1, 0, alpha, depth, layer);
    };
    // Add a text with alpha and transformation to the game
    TBGame.prototype.addTextExt = function (text, font, x, y, xscale, yscale, angle, alpha, depth, layer) {
        if (layer === undefined) {
            layer = 0;
        }
        this._elements.push({ _kind: 1, text: text, font: font, visible: true,
            x: x, y: y, xscale: xscale, yscale: yscale, angle: angle, alpha: alpha, depth: depth, layer: layer,
            _tags: [] });
        this._elementOrderChanged = true;
        return this._elements[this._elements.length - 1];
    };
    // Add an existing text token to the game
    TBGame.prototype.addTextToken = function (textToken) {
        this._elements.push(textToken);
        this._elementOrderChanged = true;
    };
    // Change the depth of a text token
    TBGame.prototype.setTextDepth = function (textToken, depth) {
        this._elementOrderChanged = (textToken.depth !== depth);
        textToken.depth = depth;
    };
    // Change the layer of a text token
    TBGame.prototype.setTextLayer = function (textToken, layer) {
        this._elementOrderChanged = (textToken.layer !== layer);
        textToken.layer = layer;
    };
    // Clears the tags of a text token
    TBGame.prototype.clearTextTags = function (textToken) {
        textToken._tags = [];
    };
    // Returns (a copy of) the tags of a text token
    TBGame.prototype.getTextTags = function (textToken) {
        var t, tags = [];
        if (textToken._tags === undefined) {
            textToken._tags = [];
        }
        for (t = textToken._tags.length - 1; t >= 0; t -= 1) {
            tags.push(textToken._tags[t]);
        }
        return tags;
    };
    // Add tags to a text token
    TBGame.prototype.addTextTags = function (textToken, tags) {
        var t;
        tags = [].concat(tags); // Such that it also works for a single value
        if (textToken._tags === undefined) {
            textToken._tags = [];
        }
        for (t = tags.length - 1; t >= 0; t -= 1) {
            if (textToken._tags.indexOf(tags[t]) < 0) {
                textToken._tags.push(tags[t]);
            }
        }
    };
    // Remove tags from a text token
    TBGame.prototype.removeTextTags = function (textToken, tags) {
        var t, ind;
        tags = [].concat(tags); // Such that it also works for a single value
        if (textToken._tags === undefined) {
            textToken._tags = [];
        }
        for (t = tags.length - 1; t >= 0; t -= 1) {
            ind = textToken._tags.indexOf(tags[t]);
            if (ind >= 0) {
                textToken._tags.splice(ind, 1);
            }
        }
    };
    // Returns all texts that contain the tag or satisfy the select function
    TBGame.prototype.findTexts = function (arg) {
        var textTokens = [], i, textToken;
        if (arg === undefined || arg === "all" || arg === "master") {
            for (i = 0; i < this._elements.length; i += 1) {
                textToken = this._elements[i];
                if (textToken !== undefined && textToken._kind === 1) {
                    textTokens.push(textToken);
                }
            }
        }
        else if (typeof arg === "function") {
            for (i = 0; i < this._elements.length; i += 1) {
                textToken = this._elements[i];
                if (textToken !== undefined && textToken._kind === 1 && arg(textToken)) {
                    textTokens.push(textToken);
                }
            }
        }
        else {
            for (i = 0; i < this._elements.length; i += 1) {
                textToken = this._elements[i];
                if (textToken !== undefined && textToken._kind === 1 && textToken._tags.indexOf(arg) >= 0) {
                    textTokens.push(textToken);
                }
            }
        }
        return textTokens;
    };
    // Find all text tokens (for compatibility)
    TBGame.prototype.findAllTexts = function () {
        return this.findTexts();
    };
    // Remove a text token from the game
    TBGame.prototype.removeText = function (textToken) {
        var ind = this._elements.indexOf(textToken);
        if (ind < 0) {
            return;
        }
        this._elements[ind] = undefined;
    };
    // Removes all text tokens that satisfy the argument (tag or function or nothing)
    TBGame.prototype.removeTexts = function (arg) {
        var i, textTokens = this.findTexts(arg);
        for (i = 0; i < textTokens.length; i += 1) {
            this.removeText(textTokens[i]);
        }
    };
    // Remove all text tokens (for compatibility)
    TBGame.prototype.removeAllTexts = function () {
        this.removeTexts();
    };
    // Counting all text tokens that satisfy the argument (tag or function or nothing)
    TBGame.prototype.countTexts = function (arg) {
        var textTokens = this.findTexts(arg);
        return textTokens.length;
    };
    // Count all text tokens (for compatibility)
    TBGame.prototype.countAllTexts = function () {
        this.countTexts();
    };
    TBGame.prototype.disablePreventDefault = function () {
        this._isPreventDefaultEnabled = false;
    };
    return TBGame;
}());
// Now generate the tbGame object
export var tbGame = new TBGame();
